import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Sparkle, ChatCircleText, Trash, Plus, Robot, ArrowRight,
} from '@phosphor-icons/react';
import { Loader2, Send, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AXEL_AVATAR = '/axel/axel-avatar.jpg';

/* -------- Markdown-lite renderer (bold + lists) -------- */
function renderContent(text) {
  if (!text) return null;
  // Split into paragraphs
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((p, i) => {
    // list detection
    const lines = p.split('\n');
    const isList = lines.every(l => /^\s*[-*•]\s+/.test(l)) && lines.length > 1;
    if (isList) {
      return (
        <ul key={i} className="list-disc pl-5 space-y-1 my-2">
          {lines.map((l, j) => (
            <li key={j} className="text-sm text-[#111827]" dangerouslySetInnerHTML={{ __html: formatInline(l.replace(/^\s*[-*•]\s+/, '')) }} />
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="text-sm text-[#111827] leading-relaxed my-2 whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: formatInline(p) }} />
    );
  });
}
function formatInline(s) {
  // Basic escape
  const esc = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Bold **text**
  return esc.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/* -------- Conversation list item -------- */
function ConvItem({ conv, active, onClick, onDelete }) {
  return (
    <div
      onClick={onClick}
      className={`group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-all border ${
        active
          ? 'bg-indigo-50 border-indigo-300 shadow-sm'
          : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
      data-testid={`conv-item-${conv.id}`}
    >
      <ChatCircleText size={16} className={active ? 'text-indigo-600 mt-0.5' : 'text-gray-400 mt-0.5'} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${active ? 'text-indigo-900' : 'text-[#111827]'}`}>
          {conv.title || 'Nouvelle conversation'}
        </p>
        <p className="text-[10px] text-gray-500">
          {new Date(conv.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition"
        data-testid={`delete-conv-${conv.id}`}
      >
        <Trash size={12} className="text-red-500" />
      </button>
    </div>
  );
}

/* -------- Suggested prompts -------- */
const SUGGESTIONS = [
  { title: "Résumé de mon activité", prompt: "Fais-moi un résumé de mon activité de cette semaine : chantiers actifs, points bloquants, réserves prioritaires." },
  { title: "Préparer ma prochaine visite", prompt: "Aide-moi à préparer ma prochaine visite de chantier : quels points contrôler, quelles réserves rappeler, quelles entreprises interpeller ?" },
  { title: "Réserves anciennes", prompt: "Liste-moi les réserves ouvertes depuis plus de 30 jours par chantier avec la priorité et la description." },
  { title: "Décisions à prendre", prompt: "Quelles décisions dois-je prendre en priorité aujourd'hui compte tenu de mes tâches en retard et de mes échéances ?" },
];

/* -------- Main page -------- */
export default function ChantixAIPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState('');
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [howToOpen, setHowToOpen] = useState(false);
  const bottomRef = useRef(null);

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [messages, streamBuf]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantix-ai/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      setStatus({ enabled: false });
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantix-ai/conversations`, { withCredentials: true });
      setConversations(res.data || []);
      return res.data || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStatus();
      await loadConversations();
      // load snapshot (ignore error)
      try {
        const snap = await axios.get(`${API}/chantix-ai/snapshot`, { withCredentials: true });
        setSnapshot(snap.data);
      } catch { /* not enabled */ }
      setLoading(false);
    })();
  }, [loadStatus, loadConversations]);

  const loadConversation = async (convId) => {
    try {
      const res = await axios.get(`${API}/chantix-ai/conversations/${convId}`, { withCredentials: true });
      setActiveConv(res.data);
      setMessages(res.data.messages || []);
    } catch {
      toast.error("Erreur chargement conversation");
    }
  };

  const createConversation = async (initialPrompt = null) => {
    try {
      const res = await axios.post(`${API}/chantix-ai/conversations`,
        { agent_id: 'assistant' }, { withCredentials: true });
      const newConv = res.data;
      setConversations(prev => [newConv, ...prev]);
      setActiveConv(newConv);
      setMessages([]);
      if (initialPrompt) {
        setTimeout(() => sendMessage(newConv.id, initialPrompt), 100);
      }
      return newConv;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de créer la conversation');
      return null;
    }
  };

  const deleteConversation = async (convId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      await axios.delete(`${API}/chantix-ai/conversations/${convId}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConv?.id === convId) {
        setActiveConv(null);
        setMessages([]);
      }
      toast.success('Conversation supprimée');
    } catch { toast.error('Erreur suppression'); }
  };

  const sendMessage = async (convId, text) => {
    if (!text.trim() || streaming) return;
    const question = text.trim();
    setInput('');
    // Add user message locally
    const localUser = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: question,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, localUser]);
    setStreaming(true);
    setStreamBuf('');

    try {
      const res = await fetch(`${API}/chantix-ai/conversations/${convId}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: question }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembled = '';

      let done = false;
      while (!done) {
        const { done: d, value } = await reader.read();
        done = d;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';
        for (const evtBlock of events) {
          const evtLine = evtBlock.split('\n').find(l => l.startsWith('event: '));
          const dataLine = evtBlock.split('\n').find(l => l.startsWith('data: '));
          if (!evtLine || !dataLine) continue;
          const evtName = evtLine.replace('event: ', '').trim();
          let payload = {};
          try { payload = JSON.parse(dataLine.replace('data: ', '')); } catch { /* ignore */ }
          if (evtName === 'delta' && payload.text) {
            assembled += payload.text;
            setStreamBuf(assembled);
          } else if (evtName === 'done') {
            setMessages(prev => [...prev, {
              id: payload.message_id,
              role: 'assistant',
              content: payload.content || assembled,
              created_at: new Date().toISOString(),
            }]);
            setStreamBuf('');
          } else if (evtName === 'error') {
            throw new Error(payload.error || 'Erreur AI');
          }
        }
      }
      // Refresh conversation list for updated title
      await loadConversations();
    } catch (e) {
      toast.error(e.message || 'Erreur streaming');
      setStreamBuf('');
    } finally {
      setStreaming(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    if (!activeConv) {
      const newConv = await createConversation();
      if (newConv) sendMessage(newConv.id, input);
    } else {
      sendMessage(activeConv.id, input);
    }
  };

  const model = useMemo(() => status?.model || 'claude', [status]);

  // Inline CSS for AXEL animations (fade-in + scale + halo pulse)
  const axelAnimStyles = (
    <style>{`
      @keyframes axel-enter {
        0%   { opacity: 0; transform: scale(0.6) translateY(6px); }
        60%  { opacity: 1; transform: scale(1.08) translateY(-2px); }
        100% { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes axel-halo {
        0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.45), 0 0 0 4px rgba(99, 102, 241, 0.10); }
        50%      { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.0), 0 0 0 4px rgba(99, 102, 241, 0.20); }
      }
      .axel-enter { animation: axel-enter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
      .axel-halo  { animation: axel-halo 1.6s ease-in-out infinite; }
    `}</style>
  );

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
        </div>
      </AppShell>
    );
  }

  if (!status?.enabled) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto py-12 px-4" data-testid="chantix-ai-locked">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
            <img src={AXEL_AVATAR} alt="AXEL"
              className="w-24 h-24 mx-auto rounded-2xl object-cover shadow-lg ring-4 ring-indigo-100 mb-4" />
            <h1 className="text-2xl font-bold text-[#111827] mb-2">AXEL</h1>
            <p className="text-sm text-[#4B5563] mb-6">
              Votre assistant intelligent CHANTIX (Assistant eXpert En Ligne) n&apos;est pas encore activé sur votre compte.
              Contactez votre administrateur pour l&apos;activer.
            </p>
            <Button onClick={() => navigate('/dashboard')} variant="outline"
              data-testid="chantix-ai-back-dashboard">
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {axelAnimStyles}
      <div className="h-[calc(100dvh-8rem)] md:h-[calc(100vh-4rem)] flex flex-col" data-testid="chantix-ai-page">
        {/* Header */}
        <div className="flex items-center gap-3 p-3 border-b border-gray-200 bg-white">
          <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden"
            onClick={() => navigate('/dashboard')} data-testid="chantix-ai-back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="w-11 h-11 rounded-xl overflow-hidden ring-2 ring-indigo-200 flex-shrink-0">
            <img src={AXEL_AVATAR} alt="AXEL" className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-[#111827] truncate" data-testid="chantix-ai-title">
              AXEL
            </h1>
            <p className="text-xs text-[#6B7280] truncate">
              Assistant eXpert En Ligne · {model.replace('anthropic/', '').replace('claude-', 'Claude ')}
            </p>
          </div>
          <Button
            size="sm" onClick={() => createConversation()} disabled={streaming}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="new-conversation-btn"
          >
            <Plus size={16} className="mr-1" /> Nouvelle
          </Button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar conversations */}
          <aside className="hidden md:flex md:w-72 flex-col border-r border-gray-200 bg-gray-50">
            <div className="p-3 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase">
                Conversations ({conversations.length})
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conv-list">
              {conversations.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">
                  Aucune conversation. Cliquez sur &quot;Nouvelle&quot; pour commencer.
                </p>
              ) : conversations.map(c => (
                <ConvItem
                  key={c.id}
                  conv={c}
                  active={activeConv?.id === c.id}
                  onClick={() => loadConversation(c.id)}
                  onDelete={() => deleteConversation(c.id)}
                />
              ))}
            </div>
          </aside>

          {/* Main chat area */}
          <main className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
              {(!activeConv || messages.length === 0) && !streaming && (
                <div className="max-w-2xl mx-auto py-8" data-testid="chantix-ai-welcome">
                  <div className="text-center mb-8">
                    <div className="relative w-28 h-28 mx-auto mb-4">
                      <img src={AXEL_AVATAR} alt="AXEL"
                        className="w-28 h-28 rounded-3xl object-cover shadow-xl ring-4 ring-indigo-100" />
                      <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full w-6 h-6 border-2 border-white flex items-center justify-center" title="En ligne">
                        <span className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-[#111827] mb-2">
                      Bonjour, je suis AXEL.
                    </h2>
                    <p className="text-sm text-[#4B5563]">
                      Votre assistant intelligent CHANTIX. Je vous accompagne au quotidien dans le suivi
                      de vos chantiers, de vos missions et de votre activité.
                    </p>
                  </div>

                  {/* Personalized snapshot */}
                  {snapshot && (
                    <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="axel-snapshot">
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
                        <p className="text-xl font-bold text-blue-700">{snapshot.chantiers_active}</p>
                        <p className="text-[11px] text-blue-600 font-medium">Chantiers actifs</p>
                      </div>
                      <div className={`p-3 border rounded-xl text-center ${snapshot.reserves_old > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                        <p className={`text-xl font-bold ${snapshot.reserves_old > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                          {snapshot.reserves_open}
                        </p>
                        <p className={`text-[11px] font-medium ${snapshot.reserves_old > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          Réserves ouvertes {snapshot.reserves_old > 0 ? `(${snapshot.reserves_old} > 30j)` : ''}
                        </p>
                      </div>
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl text-center">
                        <p className="text-xl font-bold text-orange-700">{snapshot.tasks_delayed}</p>
                        <p className="text-[11px] text-orange-600 font-medium">Tâches en retard</p>
                      </div>
                      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-center">
                        <p className="text-xl font-bold text-indigo-700">{snapshot.crs_this_month}</p>
                        <p className="text-[11px] text-indigo-600 font-medium">CR ce mois-ci</p>
                      </div>
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                        <p className="text-xl font-bold text-emerald-700">{snapshot.visites_next_7d}</p>
                        <p className="text-[11px] text-emerald-600 font-medium">Visites 7j</p>
                      </div>
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-center flex flex-col items-center justify-center">
                        <Sparkle size={20} weight="fill" className="text-purple-600 mb-0.5" />
                        <p className="text-[11px] text-purple-700 font-semibold">Je vous connais</p>
                      </div>
                    </div>
                  )}

                  {/* Comment ça marche ? */}
                  <div className="mb-6 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl overflow-hidden" data-testid="axel-howto">
                    <button
                      onClick={() => setHowToOpen(o => !o)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/40 transition"
                      data-testid="axel-howto-toggle"
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
                        <Sparkle size={18} weight="fill" className="text-indigo-600" />
                        Comment ça marche avec AXEL ?
                      </span>
                      <span className={`text-indigo-600 text-lg transition-transform ${howToOpen ? 'rotate-180' : ''}`}>▾</span>
                    </button>
                    {howToOpen && (
                      <div className="px-4 pb-4 space-y-2.5 text-sm text-[#111827]" data-testid="axel-howto-content">
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">1</span>
                          <p><strong>Posez votre question</strong> en langage naturel. Ex : <em>« Prépare ma visite du chantier X »</em> ou <em>« Quelles réserves sont ouvertes depuis plus d&apos;un mois ? »</em></p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                          <p><strong>AXEL lit vos données CHANTIX en direct</strong> (chantiers, réserves, planning, comptes rendus) et vous répond en s&apos;appuyant sur vos vraies données.</p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                          <p><strong>Enchaînez la conversation</strong> : AXEL garde le contexte. Affinez, demandez une reformulation, un ton plus direct, un tableau, une synthèse.</p>
                        </div>
                        <div className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">4</span>
                          <p><strong>Vérifiez</strong> avant tout envoi officiel. AXEL est un assistant, pas un remplaçant : le jugement OPC/CSPS reste le vôtre.</p>
                        </div>
                        <div className="mt-3 p-3 bg-white/60 rounded-lg text-xs text-[#4B5563]">
                          <strong className="text-[#111827]">Ce qu&apos;AXEL ne fait PAS :</strong> commercial, facturation, RH, marketing, réseaux sociaux. Il est 100 % dédié à votre métier.
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="suggestions-grid">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          if (activeConv) sendMessage(activeConv.id, s.prompt);
                          else createConversation(s.prompt);
                        }}
                        disabled={streaming}
                        className="p-4 text-left rounded-xl border border-gray-200 bg-white hover:border-indigo-400 hover:shadow-md transition-all group"
                        data-testid={`suggestion-${i}`}
                      >
                        <div className="flex items-start gap-2">
                          <ArrowRight size={16} className="text-indigo-500 group-hover:translate-x-1 transition-transform mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-[#111827] mb-1">{s.title}</p>
                            <p className="text-xs text-[#6B7280] line-clamp-2">{s.prompt}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m) => (
                <div key={m.id}
                  className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`msg-${m.role}-${m.id}`}>
                  {m.role === 'assistant' && (
                    <img src={AXEL_AVATAR} alt="AXEL"
                      className="axel-enter w-9 h-9 rounded-lg object-cover ring-2 ring-indigo-100 flex-shrink-0" />
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    m.role === 'user'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 border border-gray-200'
                  }`}>
                    {m.role === 'user' ? (
                      <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <div className="prose prose-sm max-w-none">{renderContent(m.content)}</div>
                    )}
                  </div>
                </div>
              ))}

              {/* Streaming assistant bubble */}
              {streaming && (
                <div className="flex gap-3 justify-start" data-testid="streaming-msg">
                  <div className="relative flex-shrink-0">
                    <img src={AXEL_AVATAR} alt="AXEL"
                      className="axel-enter axel-halo w-9 h-9 rounded-lg object-cover ring-2 ring-indigo-300" />
                  </div>
                  <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-gray-100 border border-gray-200 axel-enter">
                    {streamBuf ? (
                      <div className="prose prose-sm max-w-none">{renderContent(streamBuf)}</div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>AXEL réfléchit...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-200 bg-white">
              <div className="max-w-4xl mx-auto flex gap-2 items-end">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Posez votre question à AXEL..."
                  className="min-h-[48px] max-h-[160px] resize-none flex-1 text-sm"
                  disabled={streaming}
                  data-testid="chantix-ai-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  }}
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || streaming}
                  size="icon"
                  className="h-12 w-12 bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
                  data-testid="chantix-ai-send-btn"
                >
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                AXEL utilise vos données CHANTIX pour répondre. Vérifiez toujours les informations importantes.
              </p>
            </div>
          </main>
        </div>
      </div>
    </AppShell>
  );
}
